import React, {useMemo, useState} from 'react';
import {BsFilterRight} from 'react-icons/bs';
import FilterChip from '../../components/chip/FilterChip';
import SectionHeader from '../../components/SectionHeader';
import pages from '../../content/pages.json';
import {useArticles, useCategories} from '../../data/resources';
import {usePageMeta} from '../../seo/usePageMeta';
import {Article} from '../../types';
import {toggleItem} from '../../utils/array';
import ArticleCard from './ArticleCard';
import './news.css';

/** three fill a desktop row; on tablets the first one spans both columns */
const RECENT_COUNT = 3;
const PAGE_SIZE = 12;

const yearOf = (article: Article) => new Date(article.date).getFullYear();

const News = () => {
    usePageMeta({title: pages.news.title, description: pages.news.description});
    const {data: allArticles} = useArticles();
    const {data: categories = []} = useCategories();
    const [filterOpen, setFilterOpen] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState<number[]>([]);
    const [yearFilter, setYearFilter] = useState<number[]>([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const articles = useMemo(() => (allArticles ?? []).filter(article => article.title), [allArticles]);
    const years = useMemo(() => Array.from(new Set(articles.map(yearOf))), [articles]);
    const activeFilters = categoryFilter.length + yearFilter.length;

    // without a filter the archive continues after the recent articles; with a filter it searches all of them
    const archive = useMemo(() => {
        if (!activeFilters) return articles.slice(RECENT_COUNT);
        return articles.filter(article =>
            (!yearFilter.length || yearFilter.includes(yearOf(article))) &&
            (!categoryFilter.length || article.categories.some(id => categoryFilter.includes(id))));
    }, [articles, activeFilters, yearFilter, categoryFilter]);

    const changeFilter = (change: () => void) => {
        change();
        setVisibleCount(PAGE_SIZE);
    };

    return (
        <div className="page">
            <h1 className="page-title">News</h1>

            {!allArticles ? (
                <div className="article-grid" aria-hidden="true">
                    {Array.from({length: RECENT_COUNT}, (_, i) => <div key={i} className="article-skeleton skeleton"/>)}
                </div>
            ) : (
                <>
                    <div className="article-grid article-grid--recent">
                        {articles.slice(0, RECENT_COUNT).map(article => <ArticleCard key={article.id} article={article}/>)}
                    </div>

                    <SectionHeader title="Archiv">
                        <button type="button" className="button button--ghost" aria-expanded={filterOpen}
                                onClick={() => setFilterOpen(open => !open)}>
                            <BsFilterRight aria-hidden="true"/>
                            Filter{activeFilters ? ` (${activeFilters})` : ''}
                        </button>
                    </SectionHeader>

                    {filterOpen && (
                        <div className="card news-filter-panel">
                            <div>
                                <h3>Kategorie</h3>
                                <div className="chip-group">
                                    {categories.map(category => (
                                        <FilterChip key={category.id} label={category.name}
                                                    active={categoryFilter.includes(category.id)}
                                                    onClick={() => changeFilter(() => setCategoryFilter(current => toggleItem(current, category.id)))}/>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h3>Jahr</h3>
                                <div className="chip-group">
                                    {years.map(year => (
                                        <FilterChip key={year} label={String(year)} active={yearFilter.includes(year)}
                                                    onClick={() => changeFilter(() => setYearFilter(current => toggleItem(current, year)))}/>
                                    ))}
                                </div>
                            </div>
                            {activeFilters > 0 && (
                                <div>
                                    <button type="button" className="button button--ghost"
                                            onClick={() => changeFilter(() => {
                                                setCategoryFilter([]);
                                                setYearFilter([]);
                                            })}>
                                        Filter zurücksetzen
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="article-grid">
                        {archive.slice(0, visibleCount).map(article => <ArticleCard key={article.id} article={article}/>)}
                    </div>
                    {activeFilters > 0 && !archive.length && <p className="empty-state">Keine Artikel für diese Auswahl.</p>}
                    {archive.length > visibleCount && (
                        <div className="news-more">
                            <button type="button" className="button" onClick={() => setVisibleCount(count => count + PAGE_SIZE)}>
                                Weitere Artikel laden
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default News;
